// Load / autosave / submit for the publisher onboarding form.
//
// Much thinner than useSelfServiceDraft: the URL token IS the draft key, so
// there's no minting, no scope, and no anonymous-to-authenticated promotion.
// Autosave still matters even though this is a single page — a publisher
// gathering a logo and six video files will not finish in one sitting.

import { useCallback, useEffect, useRef, useState } from 'react';
import { publisherIntakeService } from '../../../services/publisherIntakeService';
import type {
  IntakeChainStation,
  IntakeValues,
  IntakeWirePayload,
  PublisherIntakeStatus,
} from '../../../types/publisherIntake';
import { emptyIntakeValues, toIntakePayload } from './validateIntakePayload';

/**
 * A draft round-trips the form's own string-typed inputs, but a SUBMITTED
 * payload has been coerced to numbers by toIntakePayload. Normalize on load so
 * reopening either one lands in controlled inputs.
 *
 * Keys are picked explicitly rather than spread: pre-v2 payloads carry fields
 * the form no longer has (displayName, markets, minBudget, minCampaignDays),
 * and the nested adOpsContact has to flatten into the two inputs.
 */
function hydrate(payload: Partial<IntakeWirePayload> | undefined): IntakeValues {
  const base = emptyIntakeValues();
  if (!payload) return base;
  const out: IntakeValues = { ...base };
  if (typeof payload.slug === 'string') out.slug = payload.slug;
  if (typeof payload.name === 'string') out.name = payload.name;
  if (typeof payload.logoUrl === 'string') out.logoUrl = payload.logoUrl;
  if (typeof payload.logoInvert === 'boolean') out.logoInvert = payload.logoInvert;
  if (typeof payload.logoScale === 'number') out.logoScale = payload.logoScale;
  for (const key of ['primaryColor', 'secondaryColor', 'accentColor'] as const) {
    const value = payload[key];
    if (typeof value === 'string') out[key] = value;
  }
  if (typeof payload.ronCpm === 'number') out.ronCpm = String(payload.ronCpm);
  else if (typeof payload.ronCpm === 'string') out.ronCpm = payload.ronCpm;
  if (Array.isArray(payload.backgroundClips)) out.backgroundClips = payload.backgroundClips;
  if (Array.isArray(payload.teamMembers)) out.teamMembers = payload.teamMembers;
  // Drafts round-trip the form's own FLAT keys; only toIntakePayload (submit)
  // nests them as adOpsContact. Read both shapes — missing either one is how
  // the contact silently vanished from reloaded drafts.
  if (typeof payload.adOpsEmail === 'string') out.adOpsEmail = payload.adOpsEmail;
  if (typeof payload.adOpsPhone === 'string') out.adOpsPhone = payload.adOpsPhone;
  if (typeof payload.showTeamCommission === 'boolean') {
    out.showTeamCommission = payload.showTeamCommission;
  }
  if (typeof payload.teamCommissionPercent === 'string') {
    out.teamCommissionPercent = payload.teamCommissionPercent;
  }
  if (payload.teamCommission) {
    out.showTeamCommission = payload.teamCommission.enabled === true;
    if (typeof payload.teamCommission.defaultPercent === 'number') {
      out.teamCommissionPercent = String(payload.teamCommission.defaultPercent);
    }
  }
  if (payload.adOpsContact) {
    if (typeof payload.adOpsContact.email === 'string') {
      out.adOpsEmail = payload.adOpsContact.email;
    }
    if (typeof payload.adOpsContact.phone === 'string') {
      out.adOpsPhone = payload.adOpsContact.phone;
    }
  }
  return out;
}

const SAVE_DEBOUNCE_MS = 1200;
const cacheKey = (token: string) => `ctvbuyer.publisherintake.draft.${token}`;

export interface PublisherIntakeDraft {
  isLoading: boolean;
  loadError: string | null;
  status: PublisherIntakeStatus | null;
  organizationName: string | null;
  contactName: string | null;
  submittedAt: string | null;
  /** Only populated on a consumed lookup; the live form fetches its own. */
  chain: IntakeChainStation[];
  initialValues: IntakeValues;
  save: (values: IntakeValues) => void;
  saveNow: (values: IntakeValues) => Promise<void>;
  submit: (values: IntakeValues) => Promise<void>;
  saving: boolean;
  lastSavedAt: string | null;
}

function readCache(token: string): Partial<IntakeWirePayload> | null {
  try {
    const raw = window.localStorage.getItem(cacheKey(token));
    return raw ? (JSON.parse(raw) as Partial<IntakeWirePayload>) : null;
  } catch {
    return null;
  }
}

function writeCache(token: string, values: Partial<IntakeValues>): void {
  try {
    window.localStorage.setItem(cacheKey(token), JSON.stringify(values));
  } catch {
    /* private mode / quota — the server copy is authoritative anyway */
  }
}

export function usePublisherIntakeDraft(token: string | undefined): PublisherIntakeDraft {
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [status, setStatus] = useState<PublisherIntakeStatus | null>(null);
  const [organizationName, setOrganizationName] = useState<string | null>(null);
  const [contactName, setContactName] = useState<string | null>(null);
  const [submittedAt, setSubmittedAt] = useState<string | null>(null);
  const [chain, setChain] = useState<IntakeChainStation[]>([]);
  const [initialValues, setInitialValues] = useState<IntakeValues>(emptyIntakeValues);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<IntakeValues | null>(null);

  useEffect(() => {
    if (!token) {
      setIsLoading(false);
      setLoadError('Missing onboarding link.');
      return;
    }
    // Re-arm the loading gate on every token change, not just first mount.
    // "Submit and Onboard Another Station" navigates to a sibling token in
    // the SAME mounted page — without this the form never unmounts, keeps the
    // previous station's values, and the navigation looks like nothing
    // happened.
    setIsLoading(true);
    setLoadError(null);
    setStatus(null);
    setSubmittedAt(null);
    setLastSavedAt(null);
    setChain([]);
    let cancelled = false;
    (async () => {
      try {
        const view = await publisherIntakeService.lookup(token);
        if (cancelled) return;
        setStatus(view.status);
        setOrganizationName(view.organizationName);
        setContactName(view.contactName);
        setSubmittedAt(view.submittedAt);
        setChain(view.chain ?? []);
        setInitialValues(hydrate(view.payload));
      } catch (e) {
        if (cancelled) return;
        // Crash rescue: if the lookup fails but we have a local copy, let them
        // keep working rather than losing everything they typed.
        const cached = readCache(token);
        if (cached) {
          setInitialValues(hydrate(cached));
          setStatus('draft');
        } else {
          setLoadError(e instanceof Error ? e.message : 'Could not load your onboarding form.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const flush = useCallback(async () => {
    const values = pendingRef.current;
    if (!token || !values) return;
    pendingRef.current = null;
    setSaving(true);
    try {
      const res = await publisherIntakeService.saveDraft(token, values);
      setLastSavedAt(res.savedAt);
    } catch {
      // Autosave failures are silent; the local cache still holds the work and
      // submit surfaces any real problem.
    } finally {
      setSaving(false);
    }
  }, [token]);

  const save = useCallback((values: IntakeValues) => {
    if (!token) return;
    pendingRef.current = values;
    writeCache(token, values);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { void flush(); }, SAVE_DEBOUNCE_MS);
  }, [token, flush]);

  const saveNow = useCallback(async (values: IntakeValues) => {
    if (!token) return;
    pendingRef.current = values;
    writeCache(token, values);
    if (timerRef.current) clearTimeout(timerRef.current);
    await flush();
  }, [token, flush]);

  // A pending debounce must not be lost to a tab switch or a close.
  useEffect(() => {
    const onHide = () => { if (pendingRef.current) void flush(); };
    window.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onHide);
    };
  }, [flush]);

  const submit = useCallback(async (values: IntakeValues) => {
    if (!token) throw new Error('Missing onboarding link.');
    if (timerRef.current) clearTimeout(timerRef.current);
    pendingRef.current = null;
    const res = await publisherIntakeService.submit(token, toIntakePayload(values));
    setStatus('submitted');
    setSubmittedAt(res.submittedAt);
    // The submitted values become the new baseline. The thank-you panel
    // unmounts the form, and "Edit my responses" re-mounts it seeded from
    // initialValues — without this, a form filled in THIS sitting re-opened
    // as the load-time (possibly blank) draft.
    setInitialValues(values);
  }, [token]);

  return {
    isLoading, loadError, status, organizationName, contactName, submittedAt,
    chain,
    initialValues, save, saveNow, submit, saving, lastSavedAt,
  };
}
